import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useReveal } from "@/hooks/useReveal";
import { APP_URL } from "@/lib/constants";

const CARDS = [
  { value: "10\u00A0000\u00A0₸", label: "Получите сразу после регистрации.", dark: true },
  { value: "До 60\u00A0000\u00A0₸", label: "Подарок ко Дню рождения по программе привилегий.", dark: false },
  { value: "Ранний доступ", label: "Узнавайте о новых коллекциях раньше большинства покупателей.", dark: false },
  { value: "Персональные предложения", label: "Специальные бонусы и акции только для участников клуба.", dark: false },
];

const BenefitCard = ({ card, index }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 180, damping: 22 });
  const sy = useSpring(my, { stiffness: 180, damping: 22 });
  const rotateX = useTransform(sy, [0, 1], [6, -6]);
  const rotateY = useTransform(sx, [0, 1], [-6, 6]);
  const glowX = useTransform(sx, (v) => `${v * 100}%`);
  const glowY = useTransform(sy, (v) => `${v * 100}%`);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width);
    my.set((e.clientY - rect.top) / rect.height);
  };

  const onLeave = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      data-testid={`benefit-card-${index}`}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      whileHover={{ y: -8 }}
      whileTap={{ scale: 0.96 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      style={{ rotateX, rotateY, transformPerspective: 900 }}
      className="benefit-card reveal group relative rounded-2xl overflow-hidden cursor-pointer"
    >
      <div
        className="relative p-8 md:p-9 rounded-2xl border flex flex-col justify-between min-h-[220px] h-full"
        style={{
          background: card.dark ? "var(--ink)" : "#ffffff",
          borderColor: card.dark ? "rgba(255,171,0,0.35)" : "var(--grey-3)",
          transitionDelay: `${index * 120}ms`,
        }}
      >
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none"
          style={{
            background: useTransform(
              [glowX, glowY],
              ([x, y]) =>
                `radial-gradient(260px circle at ${x} ${y}, ${
                  card.dark ? "rgba(255,171,0,0.22)" : "rgba(255,171,0,0.16)"
                }, transparent 70%)`
            ),
            transition: "opacity 0.4s ease",
          }}
        />
        <span className="benefit-shine" aria-hidden="true" />
        <div
          className="relative font-semibold transition-transform duration-500 ease-out group-hover:-translate-y-1"
          style={{
            fontSize: card.value.length > 14 ? "clamp(1.25rem, 2vw, 1.6rem)" : "clamp(1.75rem, 2.8vw, 2.5rem)",
            letterSpacing: "-0.03em",
            lineHeight: 1.1,
            color: card.dark ? "var(--accent)" : "var(--ink)",
          }}
        >
          {card.value}
        </div>
        <p
          className="relative mt-6 text-[14px] leading-relaxed"
          style={{ color: card.dark ? "rgba(255,255,255,0.6)" : "var(--grey-1)" }}
        >
          {card.label}
        </p>
      </div>
    </motion.div>
  );
};

export const Benefits = () => {
  const ref = useReveal();
  return (
    <section id="benefits" ref={ref} data-testid="benefits-section" className="py-28 md:py-36" style={{ background: "var(--paper)" }}>
      <div className="pw-container">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <span className="pw-eyebrow reveal">Привилегии</span>
            <h2 className="pw-h2 reveal mt-6 max-w-2xl">
              Почему стоит зарегистрироваться уже сегодня.
            </h2>
          </div>
          <a
            href={APP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="benefits-cta"
            className="pw-btn pw-btn-dark btn-ingot reveal self-start md:self-auto"
          >
            <span className="ingot-inner">
              <span className="ingot-label">Получить доступ</span>
              <span className="ingot-brand" aria-hidden="true">Angiolini</span>
            </span>
          </a>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" style={{ perspective: "1200px" }}>
          {CARDS.map((c, i) => (
            <BenefitCard key={i} card={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
